// /help — v1.5 (0.1.51).
//
// Renders the repo README or a specific docs page inline as the
// operator-facing help surface. Matches the hyper-scanno DESIGN.md §6
// pattern.
//
// v1 shipped: /help renders README.md.
// v1.5 (this): /help/<slug> renders a specific doc from the docs/
// tree, resolved via a fixed slug → raw-import map. The 4 docs that
// operator-facing UI links point at (PATs, ingest, howto, contract)
// are the initial members; new entries land in HELP_PAGES below.
//
// v2 is captured in FEATURE_REQUESTS.md ("Proper /help page — beyond
// rendering the repo README") and will replace this page with a
// curated, operator-authored help system.

// Vite ?raw query — the file's contents are inlined into the bundle
// as a string at build time. Docs live at the captain-tasko repo root
// (apps/web/src/pages → up four levels → captain-tasko/).
import readmeRaw from '../../../../README.md?raw';
import patsApiRaw from '../../../../docs/pats/api.md?raw';
import documentsIngestApiRaw from '../../../../docs/documents-ingest-api.md?raw';
import apiHowtoRaw from '../../../../docs/api-howto.md?raw';
import apiContractRaw from '../../../../docs/api-contract.md?raw';
import taskoMcpSetupRaw from '../../../../docs/tasko-mcp-setup.md?raw';
import nestsAuthMatrixRaw from '../../../../docs/nests-auth-matrix.md?raw';
import externalClientsRoleChangeFlowRaw from '../../../../docs/external-clients-role-change-flow.md?raw';
// CTSK-250 — named role catalog reference, linked from PatsPanel's
// "Learn more about roles →" under the Role (from catalog) dropdown.
import roleCatalogRaw from '../../../../docs/role-catalog.md?raw';
// CTSK-281 — project session refs help doc, linked from the "Sessions"
// header on Project Detail.
import projectSessionsRaw from '../../../../docs/project-sessions.md?raw';
// CTSK-TBD (child of CTSK-166) — session-identity hardening ADR, linked
// from the External Clients onboarding card's UID-discovery strip. The
// doc stays at docs/decisions/0003-…; the help slug is its frontmatter.
import sessionIdentityHardeningRaw from '../../../../docs/decisions/0003-session-identity-hardening.md?raw';
// CTSK-286 — bundles help doc, linked from the Settings → Bundles
// panel header.
import bundlesRaw from '../../../../docs/bundles.md?raw';
// CTSK-289 — tools help doc, linked from the Settings → Tools panel
// header.
import toolsRaw from '../../../../docs/tools.md?raw';
// CTSK-389 — audit view help doc, linked from the Settings → Audit
// panel header.
import auditViewRaw from '../../../../docs/audit-view.md?raw';
// CTSK-333 (Phase 3 of EPIC CTSK-385) — recycle bin help doc, linked
// from the Settings → Recycle Bin panel header.
import recycleBinRaw from '../../../../docs/recycle-bin.md?raw';
// CTSK-375..380 — Settings sub-page help docs batch (EPIC CTSK-374),
// linked from each Settings tab's page-header `?` icon.
import settingsGeneralRaw from '../../../../docs/settings-general.md?raw';
import settingsDocumentsRaw from '../../../../docs/settings-documents.md?raw';
import settingsSearchRaw from '../../../../docs/settings-search.md?raw';
import settingsDataPipelineRaw from '../../../../docs/settings-data-pipeline.md?raw';
import settingsUsersRaw from '../../../../docs/settings-users.md?raw';
import settingsGroupsRaw from '../../../../docs/settings-groups.md?raw';
import settingsOrganizationsRaw from '../../../../docs/settings-organizations.md?raw';
import settingsToolPaletteRaw from '../../../../docs/settings-tool-palette.md?raw';
// CTSK-383 — ECN operator walkthrough, linked from the External
// Clients nest page's `?` icon (was nests-auth-matrix).
import externalClientsNestRaw from '../../../../docs/external-clients-nest.md?raw';
// CTSK-381 + CTSK-382 — per-tool help docs, linked from the DSR and
// Timeline Lane tool pages' `?` icons (both were generic "tools").
import dataSourcesRegistryRaw from '../../../../docs/data-sources-registry.md?raw';
import timelineLaneRaw from '../../../../docs/timeline-lane.md?raw';
// CTSK-400 — tool onboarding standard (10-item minimum bar), the
// bundle-installer/tool-author deep reference; linked from the
// /help/tools "Tool onboarding checklist" section.
import toolOnboardingStandardRaw from '../../../../docs/tool-onboarding-standard.md?raw';

import { useEffect } from 'react';

import { MarkdownView } from '../components/MarkdownView.js';
// CTSK-356 — page metadata (title / area / description) moved to the
// exported catalog so the /help index (HelpIndex.tsx) and this page
// share one source of truth. Raw markdown content stays here: Vite
// `?raw` imports are a build-time concern this page owns.
import { HELP_PAGES } from '../lib/help-catalog.js';

/**
 * Slug → raw markdown content. Metadata (title/area/description)
 * lives in lib/help-catalog.ts; this map only binds each catalog slug
 * to its build-time-inlined doc body. The module-scope guard below
 * keeps the two in lockstep.
 */
const HELP_CONTENT: Record<string, string> = {
  'pats-api': patsApiRaw,
  'documents-ingest-api': documentsIngestApiRaw,
  'api-howto': apiHowtoRaw,
  'api-contract': apiContractRaw,
  'tasko-mcp-setup': taskoMcpSetupRaw,
  'nests-auth-matrix': nestsAuthMatrixRaw,
  'external-clients-role-change-flow': externalClientsRoleChangeFlowRaw,
  // CTSK-250 — per-role reference generated from ROLE_CATALOG; the
  // shared constants test guards handle + cap-count parity with the doc.
  'role-catalog': roleCatalogRaw,
  // CTSK-281 — project session refs help doc
  'project-sessions': projectSessionsRaw,
  // CTSK-TBD — ADR 0003 routed as a help page for the ECN onboarding card.
  'session-identity-hardening': sessionIdentityHardeningRaw,
  // CTSK-286 — bundles help doc, linked from Settings → Bundles.
  'bundles': bundlesRaw,
  // CTSK-289 — tools help doc, linked from Settings → Tools.
  'tools': toolsRaw,
  // CTSK-389 — audit view help doc, linked from Settings → Audit.
  'audit': auditViewRaw,
  // CTSK-333 (Phase 3) — recycle bin help doc, linked from Settings →
  // Recycle Bin.
  'recycle-bin': recycleBinRaw,
  // CTSK-375..380 — Settings sub-page docs (EPIC CTSK-374).
  'settings-general': settingsGeneralRaw,
  'settings-documents': settingsDocumentsRaw,
  'settings-search': settingsSearchRaw,
  'settings-data-pipeline': settingsDataPipelineRaw,
  'settings-users': settingsUsersRaw,
  'settings-groups': settingsGroupsRaw,
  'settings-organizations': settingsOrganizationsRaw,
  'settings-tool-palette': settingsToolPaletteRaw,
  // CTSK-383 — ECN operator walkthrough.
  'external-clients-nest': externalClientsNestRaw,
  // CTSK-381 + CTSK-382 — per-tool help docs (DSR + Timeline Lane).
  'data-sources-registry': dataSourcesRegistryRaw,
  'timeline-lane': timelineLaneRaw,
  // CTSK-400 — tool onboarding standard deep reference.
  'tool-onboarding-standard': toolOnboardingStandardRaw,
};

// Catalog ↔ content parity guard (dev-time loud failure; also pinned
// by HelpIndex.test.tsx in CI). A slug present in only one of the two
// means someone followed half the "adding a help page" checklist.
for (const meta of HELP_PAGES) {
  if (!(meta.slug in HELP_CONTENT)) {
    throw new Error(`help-catalog slug "${meta.slug}" has no content in Help.tsx`);
  }
}

export function Help({
  slug,
  onNavigate,
}: {
  /** Optional sub-page identifier. `undefined` renders the repo README. */
  slug?: string;
  onNavigate?: (p: string) => void;
}) {
  // README-only path: no slug, or an unknown slug (defensive fallback
  // instead of a bare 404 — the operator lands somewhere useful).
  // (Since CTSK-356 the router sends bare `/help` to HelpIndex, so the
  // no-slug branch here is defensive only.)
  const meta = slug ? HELP_PAGES.find((p) => p.slug === slug) : undefined;
  // Non-null: the module-scope parity guard above throws at load if a
  // catalog slug is missing from HELP_CONTENT.
  const page = meta
    ? { title: meta.title, content: HELP_CONTENT[meta.slug]!, sourcePath: meta.sourcePath ?? `docs/${meta.slug}.md` }
    : undefined;
  const markdownContent = page ? page.content : readmeRaw;

  // CTSK-301 — scroll-to-hash on mount / page change. The browser's
  // native scroll-to-fragment doesn't fire for SPA-rendered content
  // (the heading doesn't exist yet at document load), so once the
  // markdown for this slug is committed to the DOM, look the target up
  // ourselves. requestAnimationFrame defers past the commit so
  // getElementById can actually see the freshly rendered headings
  // (ids courtesy of rehype-slug in MarkdownView's plugin chain).
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    requestAnimationFrame(() => {
      const el = document.getElementById(hash.slice(1));
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [slug, markdownContent]); // re-fire on page change + content change

  // CTSK-301 — in-doc <a href="#other-anchor"> clicks mutate only the
  // fragment: no route change, no remount, so the effect above never
  // re-runs. Listen for hashchange and smooth-scroll to the new target.
  useEffect(() => {
    const onHashChange = () => {
      const hash = window.location.hash;
      if (!hash) return;
      const el = document.getElementById(hash.slice(1));
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  if (!slug) {
    return (
      <>
        <div className="page-header">
          <div>
            <h1 className="page-title">Help</h1>
            <div className="page-subtitle">
              Captain Tasko v{__APP_VERSION__} — README for this build
            </div>
          </div>
        </div>
        <MarkdownView content={readmeRaw} editable={false} constrainWidth={true} />
      </>
    );
  }

  if (!page) {
    return (
      <>
        <div className="page-header">
          <div>
            <h1 className="page-title">Help — page not found</h1>
            <div className="page-subtitle">
              Unknown help slug <code>{slug}</code>. Available:{' '}
              {HELP_PAGES.map(({ slug: s }) => (
                <a
                  key={s}
                  href={`/help/${s}`}
                  onClick={(e) => {
                    if (onNavigate) {
                      e.preventDefault();
                      onNavigate(`/help/${s}`);
                    }
                  }}
                  style={{ color: 'var(--accent-primary)', marginRight: 8 }}
                >
                  {s}
                </a>
              ))}
              <br />
              or return to the{' '}
              <a
                href="/help"
                onClick={(e) => {
                  if (onNavigate) {
                    e.preventDefault();
                    onNavigate('/help');
                  }
                }}
                style={{ color: 'var(--accent-primary)' }}
              >
                help index
              </a>
              .
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-header">
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
            <a
              href="/help"
              onClick={(e) => {
                if (onNavigate) {
                  e.preventDefault();
                  onNavigate('/help');
                }
              }}
              style={{ color: 'var(--text-muted)' }}
            >
              Help
            </a>
            {' / '}
            <code>{slug}</code>
          </div>
          <h1 className="page-title">{page.title}</h1>
          <div className="page-subtitle">
            Source: <code>{page.sourcePath}</code>
          </div>
        </div>
      </div>
      {/* hideFrontmatterBanner: docs under docs/ now carry an `audience:`
          frontmatter block so the help catalog can be derived from the
          files themselves rather than hand-maintained. MarkdownView's
          default banner ("frontmatter · N fields · see panel on the
          right") is written for the document viewer, which has such a
          panel; the help route does not, so the banner would render a
          pointer to something that isn't there on all 22 help pages.
          The frontmatter is build-time metadata, not reader-facing. */}
      <MarkdownView content={page.content} editable={false} constrainWidth={true} hideFrontmatterBanner={true} />
    </>
  );
}
